/**
 * Profile photos, fetched once and shared by every avatar on the page.
 *
 * The photo endpoint needs an Authorization header and an `<img src>` cannot
 * send one, so the bytes are fetched and turned into an object URL. That is the
 * cost of not serving faces from a public folder — see the route in
 * employees.routes.js for why that trade is made deliberately.
 *
 * Everything here exists because a directory page renders the same person in a
 * table row, an org chart node and a hover card at once, and a naive
 * implementation would fetch the same face three times. A module-level cache
 * keeps one object URL per employee, and an in-flight map means concurrent
 * requests for the same person share a single fetch.
 *
 * Object URLs are deliberately never revoked. They live as long as the tab and
 * cost a few kilobytes each; revoking one while another avatar still points at
 * it would show a broken image, and tracking who still holds a reference is far
 * more machinery than the memory is worth.
 */
import { getAccessToken, refreshSession, getRefreshToken } from './api.js';

const BASE = (import.meta.env.VITE_API_BASE_URL ?? '/api/v1').replace(/\/$/, '');

/** employeeId -> object URL, or null for "asked, and there is no photo". */
const cache = new Map();
const inFlight = new Map();
const listeners = new Set();

/** Avatars re-render when a face arrives, or when somebody changes theirs. */
export const onPhotoChange = (fn) => { listeners.add(fn); return () => listeners.delete(fn); };
const announce = (employeeId) => listeners.forEach((fn) => fn(employeeId));

/** What we already know, without starting a request. */
export const cachedPhoto = (employeeId) => cache.get(employeeId);

async function fetchPhoto(employeeId, retry = true) {
  const res = await fetch(`${BASE}/employees/${employeeId}/photo`, {
    headers: { ...(getAccessToken() ? { Authorization: `Bearer ${getAccessToken()}` } : {}) },
  });

  if (res.status === 401 && retry && getRefreshToken()) {
    await refreshSession();
    return fetchPhoto(employeeId, false);
  }
  // 404 is the ordinary answer for most people: they have not uploaded one.
  if (!res.ok) return null;
  return URL.createObjectURL(await res.blob());
}

/**
 * Starts loading a photo if it has not been asked for already.
 *
 * Resolves to an object URL or null. Failures cache as null so a directory of
 * 180 people without photos does not retry them on every render.
 *
 * `hasPhoto` is the important argument. Every list that renders a face already
 * knows whether there is one — the employee record carries it — and asking
 * anyway meant a page of twenty-five rows fired twenty-five requests that
 * almost all came back 404. Passing false answers without touching the network.
 */
export function loadPhoto(employeeId, hasPhoto = true) {
  if (!employeeId) return Promise.resolve(null);
  if (!hasPhoto) {
    // Remembered, so a re-render does not reconsider it.
    if (!cache.has(employeeId)) cache.set(employeeId, null);
    return Promise.resolve(null);
  }
  if (cache.has(employeeId)) return Promise.resolve(cache.get(employeeId));
  if (inFlight.has(employeeId)) return inFlight.get(employeeId);

  const p = fetchPhoto(employeeId)
    .catch(() => null)
    .then((url) => {
      cache.set(employeeId, url);
      inFlight.delete(employeeId);
      announce(employeeId);
      return url;
    });

  inFlight.set(employeeId, p);
  return p;
}

/** After an upload or a removal, so every avatar for that person catches up. */
export function invalidatePhoto(employeeId) {
  cache.delete(employeeId);
  inFlight.delete(employeeId);
  announce(employeeId);
}
